import React from 'react';
import { Button, Typography, styled, Box } from '@mui/material';
import { SvgIconProps } from '@mui/material/SvgIcon';
import { AgentDepartureHypothesis } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/Departure/helpers/types';
import { AgentPayComponentHypothesis } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/PayComponent/helpers/types';
import { formatDate } from 'helpers/dateHelper';
import {
    AgentDepartureHypothesisType,
    AGENT_DEPARTURE_HYPOTHESIS_TYPES_BY_RECURRENCE,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/Departure/helpers/constants';
import { RecurrenceEnum } from 'routes/HumanResourcesModule/Settings/HrPositionMapping/helpers/constants';
import { currencyFormatter } from 'helpers/formatHelper';
import parse from 'html-react-parser';
import { GradeAdvancementHypothesis } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/GradeAdvancement/helpers/types';
import Flex from 'ui/Flex/Flex';
import { useTranslation } from 'react-i18next';

const StyPaper = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.primary.background,
    display: 'flex',
    textAlign: 'center',
    margin: '11px',
    padding: '1rem',
    minHeight: '4rem',
    minWidth: '14rem',
}));
const StyLargePaper = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.primary.background,
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'center',
    margin: '11px',
    gap: '0.5em',
    padding: '0.5em',
    minHeight: '4rem',
    minWidth: '14rem',
    width: '100%',
}));

const isDepartureHypothesis = (
    hyp:
        | AgentDepartureHypothesis
        | AgentPayComponentHypothesis
        | GradeAdvancementHypothesis
        | undefined,
): hyp is AgentDepartureHypothesis =>
    (hyp as AgentDepartureHypothesis).agentDepartureHypothesisType !==
    undefined;
const isPayComponentHypothesis = (
    hyp:
        | AgentDepartureHypothesis
        | AgentPayComponentHypothesis
        | GradeAdvancementHypothesis
        | undefined,
): hyp is AgentPayComponentHypothesis =>
    (hyp as AgentPayComponentHypothesis).newValuesByDates !== undefined;
const isGradeAdvancementHypothesis = (
    hyp:
        | AgentDepartureHypothesis
        | AgentPayComponentHypothesis
        | GradeAdvancementHypothesis
        | undefined,
): hyp is GradeAdvancementHypothesis =>
    (hyp as GradeAdvancementHypothesis).grade !== undefined;

type Props = {
    selected: boolean;
    onClick: () => void;
    Icon: React.ElementType<SvgIconProps>;
    title: string;
    recurrence?: RecurrenceEnum;
    style?: React.CSSProperties;
    hypothesis?:
        | AgentDepartureHypothesis
        | AgentPayComponentHypothesis
        | GradeAdvancementHypothesis;
};

function RenderDepartureDetail({
    hypothesis,
    departureHypothesisTypes,
}: {
    hypothesis?:
        | AgentDepartureHypothesis
        | AgentPayComponentHypothesis
        | GradeAdvancementHypothesis;
    departureHypothesisTypes: {
        label: string;
        value: AgentDepartureHypothesisType;
    }[];
}): React.ReactElement {
    const { t } = useTranslation();
    if (!isDepartureHypothesis(hypothesis)) return <></>;

    const departureLabel = departureHypothesisTypes.find(
        (hyp) => hyp.value === hypothesis?.agentDepartureHypothesisType,
    )?.label;

    return (
        <StyPaper>
            <Typography variant="body1" sx={{ width: '100%', margin: 'auto' }}>
                {parse(
                    t('humanResources.simulation.agentDepartureDetail', {
                        departureType: departureLabel,
                        date: formatDate(new Date(hypothesis.startDate)) ?? '',
                    }),
                )}
            </Typography>
        </StyPaper>
    );
}

function RenderPayComponentDetail({
    hypothesis,
}: {
    hypothesis:
        | AgentDepartureHypothesis
        | AgentPayComponentHypothesis
        | GradeAdvancementHypothesis
        | undefined;
}): React.ReactElement {
    const { t } = useTranslation();
    if (!isPayComponentHypothesis(hypothesis)) return <></>;

    const totalValue = Object.values(hypothesis.newValuesByDates).reduce(
        (acc, curr) => acc + curr,
        0,
    );

    return (
        <StyPaper>
            <Typography variant="body1" sx={{ width: '100%', margin: 'auto' }}>
                {parse(
                    t('humanResources.simulation.agentPayComponentDetail', {
                        value: currencyFormatter(totalValue),
                    }),
                )}
            </Typography>
        </StyPaper>
    );
}

function RenderGradeAdvancementDetail({
    hypothesis,
}: {
    hypothesis:
        | AgentDepartureHypothesis
        | AgentPayComponentHypothesis
        | GradeAdvancementHypothesis
        | undefined;
}): React.ReactElement {
    const { t } = useTranslation();
    if (!isGradeAdvancementHypothesis(hypothesis)) return <></>;

    return (
        <StyLargePaper>
            <Flex sx={{ gap: '1em' }}>
                <Typography variant="body1" sx={{ fontSize: '11px' }}>
                    {t('humanResources.simulation.gradeHypothesis.startDate')}
                </Typography>
                <Typography
                    variant="body1"
                    color="primary"
                    sx={{ fontSize: '11px' }}
                >
                    {formatDate(new Date(hypothesis.startDate))}
                </Typography>
            </Flex>
            <Flex sx={{ gap: '1em' }}>
                <Typography
                    variant="body1"
                    sx={{ fontSize: '11px', width: '25%' }}
                >
                    {t('humanResources.simulation.gradeHypothesis.newGrade')}
                </Typography>
                <Typography
                    variant="body1"
                    color="primary"
                    sx={{ fontSize: '11px' }}
                >
                    {hypothesis.grade}
                </Typography>
            </Flex>
        </StyLargePaper>
    );
}

const HypothesisSelector = ({
    hypothesis = undefined,
    selected,
    onClick,
    Icon,
    title,
    style = undefined,
    recurrence = RecurrenceEnum.RECURRENT,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const departureHypothesisTypes =
        AGENT_DEPARTURE_HYPOTHESIS_TYPES_BY_RECURRENCE[recurrence].map(
            (hypType) => ({
                label: t(
                    `humanResources.simulation.agentDepartureHypothesisType.${hypType}`,
                ),
                value: hypType,
            }),
        );

    return (
        <Button
            variant="outlined"
            onClick={onClick}
            sx={{
                ...style,
                width: '100%',
                height: 165,
                m: 1,
                p: 1,
                fontSize: '0.8rem',
                flexDirection: 'column',
                color: 'black',
                borderWidth: selected ? 3 : 1,
                '&:hover': {
                    borderWidth: selected ? 3 : 1,
                },
            }}
        >
            <Icon fontSize="large" />
            {title}
            <>
                {hypothesis && (
                    <>
                        <RenderDepartureDetail
                            hypothesis={hypothesis}
                            departureHypothesisTypes={departureHypothesisTypes}
                        />
                        <RenderPayComponentDetail hypothesis={hypothesis} />
                    </>
                )}

                {hypothesis && (
                    <RenderGradeAdvancementDetail hypothesis={hypothesis} />
                )}
            </>
        </Button>
    );
};
export default HypothesisSelector;
